import { useCallback, useMemo } from 'react';
import { formatTime } from 'utils/date-helpers';
import { AssignmentTypeEnum, CaseTypeEnum } from 'api/model';
import { useSetupGetBasicData } from 'api/setup/setup';
import { getNameFromEnumNumber } from 'utils/enum-helpers';

const addMillisToDate = (date: Date, millis: number) => {
  return new Date(date.getTime() + millis);
};

type UseResultingStartAndEndTimeProps = {
  caseType?: CaseTypeEnum;
  assignmentType?: AssignmentTypeEnum;
  bookedTo?: Date;
  estimatedWorkTime?: number;
  estimatedStartTime?: Date;
};

type GetResultingStartAndEndTimeProps = UseResultingStartAndEndTimeProps & {
  timeDistributionDictionary?: { [key: string]: number };
};

export const calculateResultingStartAndEndTime = ({
  bookedTo,
  caseType,
  assignmentType,
  estimatedWorkTime,
  estimatedStartTime,
  timeDistributionDictionary,
}: GetResultingStartAndEndTimeProps): {
  startTime: Date | null;
  endTime: Date | null;
} => {
  if (!bookedTo || !estimatedWorkTime)
    return { startTime: null, endTime: null };

  let startTime: Date | null = null;
  let endTime: Date | null = null;

  if (estimatedStartTime) {
    // we already have an estimated start time, so we can calculate the end time
    startTime = new Date(estimatedStartTime.getTime());
    endTime = addMillisToDate(estimatedStartTime, estimatedWorkTime);
  } else if (timeDistributionDictionary) {
    // we don't have an estimated start time, but we have a time distribution dictionary
    // so we can calculate the start time

    const timeDistributionKey = `${getNameFromEnumNumber(
      CaseTypeEnum,
      caseType!
    )}_${getNameFromEnumNumber(AssignmentTypeEnum, assignmentType!)}`;
    const percentageOfTimeSpentBeforeArrival =
      timeDistributionDictionary[timeDistributionKey];
    if (percentageOfTimeSpentBeforeArrival) {
      // adjust the start time to distribute
      const amountOfTimeSpentBeforeArrival =
        estimatedWorkTime * percentageOfTimeSpentBeforeArrival;
      startTime = addMillisToDate(bookedTo, -amountOfTimeSpentBeforeArrival);
      endTime = addMillisToDate(startTime, estimatedWorkTime);
    }
  }

  return { startTime, endTime };
};

const useResultingStartAndEndTime = ({
  caseType,
  assignmentType,
  bookedTo,
  estimatedWorkTime,
  estimatedStartTime,
}: UseResultingStartAndEndTimeProps) => {
  const basicDataResponse = useSetupGetBasicData();

  const { startTime: resultingStartTime, endTime: resultingEndTime } =
    useMemo(() => {
      return calculateResultingStartAndEndTime({
        caseType,
        assignmentType,
        bookedTo,
        estimatedWorkTime,
        estimatedStartTime,
        timeDistributionDictionary:
          basicDataResponse.data?.timeDistributionDictionary,
      });
    }, [
      assignmentType,
      basicDataResponse.data?.timeDistributionDictionary,
      bookedTo,
      caseType,
      estimatedWorkTime,
      estimatedStartTime,
    ]);

  const startAndEndTimeString = useMemo(() => {
    if (!resultingStartTime || !resultingEndTime) return '';
    return `${formatTime(resultingStartTime)} - ${formatTime(
      resultingEndTime
    )}`;
  }, [resultingStartTime, resultingEndTime]);

  const calculateResultingStartAndEndTimeWithoutEstimatedStartTime =
    useCallback(() => {
      return calculateResultingStartAndEndTime({
        caseType,
        assignmentType,
        bookedTo,
        estimatedWorkTime,
        timeDistributionDictionary:
          basicDataResponse.data?.timeDistributionDictionary,
      });
    }, [
      assignmentType,
      basicDataResponse.data?.timeDistributionDictionary,
      bookedTo,
      caseType,
      estimatedWorkTime,
    ]);

  return {
    resultingStartTime,
    resultingEndTime,
    startAndEndTimeString,
    calculateResultingStartAndEndTimeWithoutEstimatedStartTime,
  };
};

export default useResultingStartAndEndTime;
